<template>
  <div class="card mb-4">
    <h5 class="card-header d-flex justify-content-between align-items-center">
      <span><i class="fas fa-exclamation-circle mr-1"></i>Carrier Incidents</span>
      <small class="text-muted" v-if="total !== null">{{ total }} total · {{ activeCount }} active</small>
    </h5>
    <div class="card-body p-0">
      <div v-if="loading" class="text-center p-3">
        <i class="fas fa-spinner fa-spin"></i> Loading…
      </div>
      <div v-else-if="!incidents.length" class="text-center p-3 text-muted">
        No incidents recorded for this carrier.
      </div>
      <table v-else class="table mb-0">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Score Impact</th>
            <th>Source</th>
            <th>Order</th>
            <th>Status</th>
            <th>Recorded By</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="i in incidents" :key="i.id" :class="{ 'incident-reversed': i.isReversed }">
            <td>{{ i.occurredAt | formatDate }}</td>
            <td>{{ formatType(i.incidentType) }}</td>
            <td :class="i.scoreImpact < 0 ? 'text-danger' : ''"><strong>{{ i.scoreImpact }}</strong></td>
            <td>{{ formatSource(i.sourceType) }}</td>
            <td>
              <router-link v-if="i.leadId" :to="{ name: 'OrderDetails', params: { id: i.leadId } }">
                #{{ i.leadId }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td>
              <span v-if="i.isReversed" class="badge badge-secondary">Reversed</span>
              <span v-else-if="!i.reviewedAt" class="badge badge-warning">Unreviewed</span>
              <span v-else class="badge badge-success">Reviewed</span>
            </td>
            <td>{{ i.recordedBy ? i.recordedBy.name : '-' }}</td>
            <td class="text-right">
              <button
                v-if="!i.isReversed"
                class="btn btn-sm btn-outline-primary mr-1"
                @click="openEdit(i)"
                title="Edit"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                v-if="!i.isReversed && !i.reviewedAt"
                class="btn btn-sm btn-outline-success mr-1"
                @click="markReviewed(i)"
                title="Mark reviewed"
              >
                <i class="fas fa-check"></i>
              </button>
              <button
                v-if="!i.isReversed"
                class="btn btn-sm btn-outline-danger"
                @click="openReverse(i)"
                title="Reverse"
              >
                <i class="fas fa-undo"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CarrierIncidentEditModal
      v-if="editIncident"
      :incident="editIncident"
      :on-close="closeEdit"
      @saved="onSaved"
    />

    <CarrierIncidentReverseModal
      v-if="reverseIncident"
      :incident="reverseIncident"
      :on-close="closeReverse"
      @reversed="onSaved"
    />
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import CarrierIncidentEditModal from './CarrierIncidentEditModal.vue';
import CarrierIncidentReverseModal from './CarrierIncidentReverseModal.vue';

export default {
  name: 'CarrierIncidentsTable',
  components: { CarrierIncidentEditModal, CarrierIncidentReverseModal },
  props: {
    carrierId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      incidents: [],
      total: null,
      editIncident: null,
      reverseIncident: null,
    };
  },
  computed: {
    activeCount() {
      return this.incidents.filter(i => !i.isReversed).length;
    },
  },
  async mounted() {
    await this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      const response = await ApiClient.carriers.incidents({ id: this.carrierId, params: { per_page: 100 } });
      this.loading = false;
      if (response.status === 200) {
        this.incidents = response.data.data || [];
        this.total = response.data.total != null ? response.data.total : this.incidents.length;
      }
    },
    formatType(t) {
      const map = {
        cancellation: 'Cancellation (before window)',
        cancellation_24h: 'Cancellation (within 24h)',
        no_show: 'No-show (after window)',
        ghosting: 'Ghosting',
        late_pickup: 'Late pickup',
        late_delivery: 'Late delivery',
        communication: 'Failure to update',
        false_status: 'False status update',
        damage: 'Damage',
        damage_severe: 'Damage (severe)',
      };
      return map[t] || t;
    },
    formatSource(s) {
      const map = {
        undispatch_event: 'Undispatch',
        manual: 'Manual',
        claim: 'Claim',
        system: 'System',
      };
      return map[s] || s;
    },
    openEdit(incident) {
      this.editIncident = incident;
    },
    closeEdit() {
      this.editIncident = null;
    },
    openReverse(incident) {
      this.reverseIncident = incident;
    },
    closeReverse() {
      this.reverseIncident = null;
    },
    async markReviewed(incident) {
      const response = await ApiClient.carrierIncidents.review({ id: incident.id });
      if (response.status === 200) {
        await this.fetch();
        this.$emit('changed');
      }
    },
    async onSaved() {
      this.closeEdit();
      this.closeReverse();
      await this.fetch();
      this.$emit('changed');
    },
  },
};
</script>

<style lang="scss" scoped>
.incident-reversed {
  opacity: 0.55;
  text-decoration: line-through;
}
.incident-reversed strong {
  text-decoration: line-through;
}
</style>
